import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Bookmark, Star, FileDown, Sparkles,
  Send, Target, ListChecks, PlayCircle, RotateCcw, ExternalLink,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BusinessMasteryCard } from "@/components/BusinessMasteryCard";
import { isRelevantLesson } from "@/lib/businessMastery";

function VideoPlayer({ video }) {
  if (!video) return null;
  const isUrl = video.startsWith("http");
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video mb-6" data-testid="lesson-video">
      {isUrl ? (
        <video src={video} controls className="w-full h-full" />
      ) : (
        <iframe title="lesson-video" className="w-full h-full"
          src={`https://www.youtube.com/embed/${video}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      )}
    </div>
  );
}

function Quiz({ lessonId, quiz, initialResult, onPassChange }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(initialResult);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < quiz.length) { toast.error("Answer all questions first"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/lms/lessons/${lessonId}/quiz`, { answers: quiz.map((_, i) => answers[i]) });
      setResult(data); onPassChange?.(data.passed);
      toast[data.passed ? "success" : "error"](data.passed ? `Passed with ${data.pct}%!` : `Scored ${data.pct}% — need ${data.pass_pct}% to pass.`);
    } catch (e) { toast.error("Could not submit quiz"); } finally { setSubmitting(false); }
  };
  const retake = () => { setResult(null); setAnswers({}); };

  if (result?.results) {
    return (
      <div data-testid="quiz-result" className="space-y-4">
        <div className={`rounded-xl p-4 ${result.passed ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
          <div className={`font-heading text-2xl ${result.passed ? "text-emerald-400" : "text-red-400"}`} data-testid="quiz-score">{result.pct}%</div>
          <div className="text-sm text-[#A1A1AA]">{result.score}/{result.total} correct · {result.passed ? "Passed" : `Need ${result.pass_pct}% to pass`}</div>
        </div>
        {result.results.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/8 bg-[#121214] p-4">
            <div className="text-sm text-white font-medium mb-2">{i + 1}. {r.q}</div>
            <div className="space-y-1.5">
              {r.options.map((opt, oi) => {
                const isCorrect = oi === r.correct_answer;
                const isYours = oi === r.your_answer;
                return (
                  <div key={oi} className={`text-sm px-3 py-1.5 rounded-lg ${isCorrect ? "bg-emerald-500/15 text-emerald-300" : isYours ? "bg-red-500/15 text-red-300" : "text-[#A1A1AA]"}`}>
                    {opt}{isCorrect && " ✓"}{isYours && !isCorrect && " (your answer)"}
                  </div>
                );
              })}
            </div>
            {r.explanation && <p className="text-xs text-[#71717A] mt-2 italic">{r.explanation}</p>}
          </div>
        ))}
        <Button onClick={retake} data-testid="quiz-retake-btn" variant="outline" className="border-white/15 bg-transparent text-white rounded-full"><RotateCcw className="w-4 h-4 mr-1" /> Retake quiz</Button>
      </div>
    );
  }

  return (
    <div data-testid="quiz-form" className="space-y-4">
      {quiz.map((q, i) => (
        <div key={i} className="rounded-xl border border-white/8 bg-[#121214] p-4">
          <div className="text-sm text-white font-medium mb-2">{i + 1}. {q.q}</div>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))} data-testid={`quiz-q${i}-opt${oi}`}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${answers[i] === oi ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:border-white/25"}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <Button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Submit quiz</Button>
    </div>
  );
}

function AiCoach({ lessonId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const ask = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setLoading(true);
    try {
      const { data } = await api.post("/lms/ai", { lesson_id: lessonId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "AI coach unavailable"); }
    finally { setLoading(false); }
  };
  return (
    <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5" data-testid="lesson-ai-coach">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[#D4AF37]" /><h4 className="font-heading text-base text-white">Ask your AI Business Coach</h4></div>
      <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
        {messages.length === 0 && <p className="text-xs text-[#71717A]">Ask anything about this lesson and how it applies to your business.</p>}
        {messages.map((m, i) => (
          <div key={i} className={`text-sm rounded-xl px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-[#D4AF37]/15 text-white ml-6" : "bg-[#1C1C1F] text-[#E4E4E7] mr-6"}`}>{m.content}</div>
        ))}
        {loading && <div className="text-xs text-[#71717A] mr-6">Coach is thinking…</div>}
      </div>
      <form onSubmit={ask} className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="lesson-ai-input" placeholder="Ask the coach…"
          className="flex-1 bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/40" />
        <Button type="submit" disabled={loading || !input.trim()} data-testid="lesson-ai-send" className="fv-gold-gradient text-[#0A0A0B] h-10 w-10 p-0 rounded-lg shrink-0"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}

export function AcademyLesson({ lessonId, onBack, onNavigate, onOpenWorkflow, onChanged }) {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const timeRef = useRef(0);

  const load = useCallback(() => {
    api.get(`/lms/lessons/${lessonId}`).then(({ data }) => { setD(data); setNotes(data.notes || ""); setNotesDirty(false); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load lesson"));
  }, [lessonId]);
  useEffect(() => { load(); }, [load]);

  // Track time on the lesson (batched every 30s).
  useEffect(() => {
    const id = setInterval(() => {
      timeRef.current += 30;
      api.post(`/lms/lessons/${lessonId}/time`, { seconds: 30 }).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [lessonId]);

  // Autosave notes (debounced).
  useEffect(() => {
    if (!notesDirty) return;
    const t = setTimeout(() => {
      api.post(`/lms/lessons/${lessonId}/notes`, { content: notes }).then(() => setNotesDirty(false)).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [notes, notesDirty, lessonId]);

  const complete = async () => {
    try {
      const { data } = await api.post(`/lms/lessons/${lessonId}/complete`);
      onChanged?.();
      if (data.course_completed) toast.success("Course complete! Your certificate is ready.");
      else toast.success("Lesson complete!");
      load();
    } catch { toast.error("Could not mark complete"); }
  };
  const toggleBookmark = async () => { await api.post(`/lms/lessons/${lessonId}/bookmark`, { value: !d.bookmarked }); setD((x) => ({ ...x, bookmarked: !x.bookmarked })); };
  const toggleFav = async () => { await api.post(`/lms/lessons/${lessonId}/favorite`, { value: !d.favorite }); setD((x) => ({ ...x, favorite: !x.favorite })); };
  const downloadResource = (index, title) => {
    const url = `${API}/lms/lessons/${lessonId}/resources/${index}/download`;
    window.open(url, "_blank");
    toast.success(`Downloading ${title}`);
  };

  if (!d) return <p className="text-[#71717A]">Loading lesson…</p>;

  return (
    <div className="fv-fade-up" data-testid="academy-lesson">
      <button onClick={onBack} data-testid="lesson-back-btn" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to course</button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{d.course_title} · Lesson {d.number} of {d.total} · {d.minutes} min</div>
          <h1 className="font-heading text-3xl text-white mb-4">{d.title}</h1>

          <VideoPlayer video={d.video} />

          {d.objectives?.length > 0 && (
            <div className="rounded-xl border border-white/8 bg-[#121214] p-4 mb-6" data-testid="lesson-objectives">
              <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#D4AF37]" /><h4 className="text-sm font-semibold text-white">Learning objectives</h4></div>
              <ul className="space-y-1">{d.objectives.map((o, i) => <li key={i} className="text-sm text-[#A1A1AA] flex gap-2"><span className="text-[#D4AF37]">•</span>{o}</li>)}</ul>
            </div>
          )}

          <div className="prose-invert text-[#D4D4D8] text-[15px] leading-relaxed whitespace-pre-wrap mb-6" data-testid="lesson-body">{d.body}</div>

          {d.resources?.length > 0 && (
            <div className="mb-6" data-testid="lesson-resources">
              <h4 className="text-sm font-semibold text-white mb-2">Downloadable resources</h4>
              <div className="flex flex-wrap gap-2">
                {d.resources.map((r) => (
                  <button key={r.index} onClick={() => downloadResource(r.index, r.title)} data-testid={`resource-${r.index}`}
                    className="flex items-center gap-2 text-sm text-[#D4AF37] border border-[#D4AF37]/25 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors"><FileDown className="w-4 h-4" /> {r.title}</button>
                ))}
              </div>
            </div>
          )}

          {d.tasks?.length > 0 && (
            <div className="rounded-xl border border-white/8 bg-[#121214] p-4 mb-6" data-testid="lesson-tasks">
              <div className="flex items-center gap-2 mb-2"><ListChecks className="w-4 h-4 text-[#D4AF37]" /><h4 className="text-sm font-semibold text-white">Practical action steps</h4></div>
              <ul className="space-y-1.5">{d.tasks.map((t) => <li key={t.id} className="text-sm text-[#A1A1AA] flex gap-2"><span className="text-[#D4AF37]">→</span>{t.label}</li>)}</ul>
            </div>
          )}

          {d.quiz?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-xl text-white mb-3">Knowledge check</h3>
              <Quiz lessonId={lessonId} quiz={d.quiz} initialResult={d.quiz_result?.results ? d.quiz_result : null} />
            </div>
          )}

          {d.workflow && (
            <button onClick={() => onOpenWorkflow(d.workflow.path)} data-testid="lesson-workflow-cta"
              className="w-full flex items-center justify-between rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-4 mb-6 hover:border-[#D4AF37]/50 transition-colors">
              <span className="flex items-center gap-2 text-white text-sm font-medium"><PlayCircle className="w-5 h-5 text-[#D4AF37]" /> Apply it now: {d.workflow.label}</span>
              <ExternalLink className="w-4 h-4 text-[#D4AF37]" />
            </button>
          )}

          {!d.next_lesson_id && isRelevantLesson({ courseTitle: d.course_title, lessonTitle: d.title }) && (
            <div className="mb-6" data-testid="lesson-bm-reading">
              <BusinessMasteryCard variant="reading" source="academy_lesson"
                course={d.course_slug} lesson={d.lesson_id} location="lesson_end" />
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-white/8 pt-5">
            <Button onClick={() => d.prev_lesson_id && onNavigate(d.prev_lesson_id)} disabled={!d.prev_lesson_id} data-testid="lesson-prev-btn" variant="outline" className="border-white/15 bg-transparent text-white rounded-full disabled:opacity-40"><ArrowLeft className="w-4 h-4 mr-1" /> Previous</Button>
            {!d.completed ? (
              <Button onClick={complete} data-testid="lesson-complete-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark complete</Button>
            ) : (
              <span className="flex items-center gap-1.5 text-sm text-emerald-400" data-testid="lesson-completed-badge"><CheckCircle2 className="w-4 h-4" /> Completed</span>
            )}
            <Button onClick={() => d.next_lesson_id && onNavigate(d.next_lesson_id)} disabled={!d.next_lesson_id} data-testid="lesson-next-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full disabled:opacity-40">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button onClick={toggleBookmark} data-testid="lesson-bookmark-btn" className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded-xl border py-2.5 transition-colors ${d.bookmarked ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}><Bookmark className="w-4 h-4" /> {d.bookmarked ? "Saved" : "Bookmark"}</button>
            <button onClick={toggleFav} data-testid="lesson-favorite-btn" className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded-xl border py-2.5 transition-colors ${d.favorite ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}><Star className="w-4 h-4" /> {d.favorite ? "Favorite" : "Favorite"}</button>
          </div>

          <div className="rounded-2xl border border-white/8 bg-[#121214] p-4" data-testid="lesson-notes">
            <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-semibold text-white">My notes</h4>{notesDirty && <span className="text-[10px] text-[#71717A]">Saving…</span>}</div>
            <textarea value={notes} onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }} data-testid="lesson-notes-input"
              placeholder="Take notes on this lesson… (auto-saved)"
              className="w-full h-40 bg-[#0d0d0f] border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-[#52525B] resize-none focus:outline-none focus:border-[#D4AF37]/40" />
          </div>

          <AiCoach lessonId={lessonId} />
        </div>
      </div>
    </div>
  );
}
